import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { StatutFacture, TypeFacture } from './useFormateurs';

// ---------------------------------------------------------------------------
// Workflow de validation et transmission à la Direction pour paiement (TF-401),
// écran « Circuit & transmission ». Ce qui empêche chaque geste — valider,
// renvoyer, transmettre, payer, refuser, annuler — vient de l'API, calculé
// pour le compte qui lit : l'écran ne décide rien.
// ---------------------------------------------------------------------------

export interface LigneFactureCircuit {
  id: string;
  numero: string;
  formateur: { id: string; nom: string };
  siteCode: string;
  type: TypeFacture | null;
  typeLibelle: string | null;
  debut: string;
  fin: string;
  montant: number;
  heuresValidees: number;
  tauxHoraire: number | null;
  montantAttendu: number | null;
  nombreFiches: number;
  statut: StatutFacture;
  etatLibelle: string;
  etatTon: string;
  transmiseLe: string;
  verifieeLe: string | null;
  verifieePar: string | null;
  valideeLe: string | null;
  valideePar: string | null;
  lot: { id: string; libelle: string; transmisLe: string } | null;
  payeeLe: string | null;
  referencePaiement: string | null;
  refusValidation: string | null;
  refusRenvoi: string | null;
  refusPaiement: string | null;
  refusRefus: string | null;
  refusAnnulationPaiement: string | null;
}

export interface CoordonneesPaiement {
  siret: string;
  iban: string | null;
  bic: string | null;
  titulaireCompte: string | null;
  nomFichier: string;
  typeMime: string;
}

/** Un contrôle SIRET, RIB, montant, séquences ou période (TF-402) : un signal, jamais bloquant. */
export interface ControleFacture {
  code: 'SIRET' | 'RIB' | 'MONTANT' | 'SEQUENCES' | 'PERIODE';
  libelle: string;
  ok: boolean;
  detail: string;
}

export interface DossierFacture extends LigneFactureCircuit, CoordonneesPaiement {
  controles: ControleFacture[];
  alertes: number;
  fiches: { id: string; titre: string; date: string | null; heures: number; typeLibelle: string }[];
  piecesNonConformes: string[];
  historique: {
    id: string;
    geste: string;
    libelle: string;
    le: string;
    auteur: string | null;
    motif: string | null;
    payeeLe: string | null;
    referencePaiement: string | null;
  }[];
}

export interface PropositionLot {
  cle: string;
  siteCode: string;
  type: TypeFacture;
  typeLibelle: string;
  factures: LigneFactureCircuit[];
  montant: number;
  rythme: string;
  du: boolean;
  precision: string;
  refusTransmission: string | null;
}

export type EtatLot = 'TRANSMIS' | 'PARTIEL' | 'PAYE' | 'REFUSE';

export interface LotAffiche {
  id: string;
  libelle: string;
  siteCode: string;
  type: TypeFacture;
  typeLibelle: string;
  transmisLe: string;
  transmisPar: string | null;
  nombreFactures: number;
  montant: number;
  montantPaye: number;
  refusees: number;
  etat: EtatLot;
  etatLibelle: string;
  etatTon: string;
}

export interface DetailLot extends LotAffiche {
  factures: (LigneFactureCircuit & CoordonneesPaiement)[];
  facturesRefusees: {
    factureId: string;
    numero: string;
    formateur: string;
    le: string;
    auteur: string | null;
    motif: string;
  }[];
  refusPaiementLot: string | null;
}

export interface CompteMontant {
  nombre: number;
  montant: number;
}

export interface VueCircuit {
  etapes: {
    fichesACompleter: number;
    fichesAValider: number;
    fichesAFacturer: number;
    facturesACorriger: number;
    facturesAVerifier: number;
    facturesAValider: number;
    facturesATransmettre: number;
    facturesChezDirection: number;
  };
  aValider: LigneFactureCircuit[];
  lotsATransmettre: PropositionLot[];
  lots: LotAffiche[];
  suivi: {
    payeesCeMois: CompteMontant;
    enAttenteDirection: CompteMontant;
    aVerifier: CompteMontant;
    renvoyees: CompteMontant;
  };
  rythmes: { type: TypeFacture; libelle: string; detail: string }[];
  peutValider: boolean;
  peutPayer: boolean;
}

const CLE = ['factures-formateurs'];

function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

export function useCircuitFactures(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: [...CLE, 'vue', parametres],
    queryFn: () => appelApi<VueCircuit>(`/factures-formateurs${parametres}`),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
}

/** Le dossier d'une facture : IBAN en clair, sa lecture est tracée. */
export function useDossierFacture(factureId: string | null) {
  return useQuery({
    queryKey: [...CLE, 'facture', factureId],
    queryFn: () =>
      appelApi<{ facture: DossierFacture }>(`/factures-formateurs/factures/${encodeURIComponent(factureId ?? '')}`),
    enabled: factureId !== null,
    gcTime: 0,
  });
}

/** Le détail d'un lot : IBAN en clair, sa lecture est tracée. */
export function useDetailLot(lotId: string | null) {
  return useQuery({
    queryKey: [...CLE, 'lot', lotId],
    queryFn: () => appelApi<{ lot: DetailLot }>(`/factures-formateurs/lots/${encodeURIComponent(lotId ?? '')}`),
    enabled: lotId !== null,
    gcTime: 0,
  });
}

/** Après un geste : l'écran, les tableaux de bord et les fiches formateur se relisent. */
function useApresGeste() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: CLE });
    void client.invalidateQueries({ queryKey: ['financements'] });
    void client.invalidateQueries({ queryKey: ['formateurs'] });
  };
}

function urlFacture(factureId: string, geste: string): string {
  return `/factures-formateurs/factures/${encodeURIComponent(factureId)}/${geste}`;
}

export function useValiderFactureFormateur() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (factureId: string) =>
      appelApi<{ facture: LigneFactureCircuit }>(urlFacture(factureId, 'validation'), { methode: 'POST' }),
    onSuccess: apres,
  });
}

export function useRenvoyerFactureFormateur() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ factureId, motif }: { factureId: string; motif: string }) =>
      appelApi<{ facture: LigneFactureCircuit }>(urlFacture(factureId, 'renvoi'), {
        methode: 'POST',
        corps: { motif },
      }),
    onSuccess: apres,
  });
}

export function useTransmettreLot() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (factureIds: string[]) =>
      appelApi<{ lot: LotAffiche }>('/factures-formateurs/lots', { methode: 'POST', corps: { factureIds } }),
    onSuccess: apres,
  });
}

export interface SaisiePaiement {
  payeeLe: string;
  reference: string;
}

export function usePayerFacture() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ factureId, saisie }: { factureId: string; saisie: SaisiePaiement }) =>
      appelApi<{ facture: LigneFactureCircuit }>(urlFacture(factureId, 'paiement'), {
        methode: 'POST',
        corps: { payeeLe: saisie.payeeLe, reference: saisie.reference.trim() || null },
      }),
    onSuccess: apres,
  });
}

export function usePayerLot() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ lotId, factureIds, saisie }: { lotId: string; factureIds: string[]; saisie: SaisiePaiement }) =>
      appelApi<{ lot: LotAffiche }>(`/factures-formateurs/lots/${encodeURIComponent(lotId)}/paiement`, {
        methode: 'POST',
        corps: { payeeLe: saisie.payeeLe, reference: saisie.reference.trim() || null, factureIds },
      }),
    onSuccess: apres,
  });
}

export function useRefuserFactureFormateur() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ factureId, motif }: { factureId: string; motif: string }) =>
      appelApi<{ facture: LigneFactureCircuit }>(urlFacture(factureId, 'refus'), {
        methode: 'POST',
        corps: { motif },
      }),
    onSuccess: apres,
  });
}

export function useAnnulerPaiementFormateur() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ factureId, motif }: { factureId: string; motif: string }) =>
      appelApi<{ facture: LigneFactureCircuit }>(urlFacture(factureId, 'paiement/annulation'), {
        methode: 'POST',
        corps: { motif },
      }),
    onSuccess: apres,
  });
}
